import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, ThemeEvents } from '@theme/events';

class CartCrossSell extends HTMLElement {
  /** @type {number} */
  #loadId = 0;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Array<Record<string, any>>} */
  #products = [];

  /** @type {number | undefined} */
  #reloadTimer;

  /** @type {MutationObserver | null} */
  #morphObserver = null;

  /** @type {boolean} */
  #updating = false;

  connectedCallback() {
    this.#cacheElements();
    this.#bindEvents();
    this.#observeMorphReset();
    this.#scheduleLoad(0);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#unbindEvents();
    this.#morphObserver?.disconnect();
    this.#morphObserver = null;
    if (this.#reloadTimer) window.clearTimeout(this.#reloadTimer);
  }

  #bindEvents() {
    this.addEventListener('click', this.#onClick);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#dialog?.addEventListener('toggle', this.#onDialogToggle);
  }

  #unbindEvents() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#dialog?.removeEventListener('toggle', this.#onDialogToggle);
  }

  get #dialog() {
    return this.closest('dialog');
  }

  /**
   * Cart morphing can replace our rendered track with empty server markup.
   * Re-apply cached products (or reload) when that happens — but ignore our own DOM writes.
   */
  #observeMorphReset() {
    this.#morphObserver?.disconnect();
    this.#morphObserver = new MutationObserver(() => {
      if (this.#updating) return;

      this.#cacheElements();
      if (!(this.track instanceof HTMLElement)) return;

      const hasProducts = this.track.querySelector('[data-variant-id]') != null;
      const hasSkeleton = this.track.querySelector('.cart-cross-sell__item--skeleton') != null;
      const isEmpty = this.track.childElementCount === 0;

      if (hasProducts) return;

      if (this.#products.length) {
        this.#renderItems(this.#products);
        this.#setupProgress();
        return;
      }

      if (hasSkeleton || isEmpty) {
        this.#scheduleLoad(100);
      }
    });

    this.#morphObserver.observe(this, { childList: true, subtree: true });
  }

  #onDialogToggle = () => {
    if (this.#dialog?.open) this.#scheduleLoad(0);
  };

  get #isProductMode() {
    return this.dataset.mode === 'product';
  }

  #onCartUpdate = (event) => {
    // Recommendations are pinned to a single product, not the cart contents.
    if (this.#isProductMode) return;

    // After collection quick-add / any cart morph, wait for section HTML to settle.
    const source =
      event?.detail?.data?.source ||
      (event instanceof CartAddEvent ? event.detail?.data?.source : null);
    const delay = source === 'cart-cross-sell' ? 200 : source === 'dm-inline-size-quick-add' ? 250 : 100;
    this.#scheduleLoad(delay);
  };

  /**
   * @param {number} delay
   */
  #scheduleLoad(delay = 0) {
    if (this.#reloadTimer) window.clearTimeout(this.#reloadTimer);
    this.#reloadTimer = window.setTimeout(() => {
      this.#reloadTimer = undefined;
      this.#startLoad();
    }, delay);
  }

  #startLoad() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#load(this.#abortController.signal, ++this.#loadId);
  }

  #cacheElements() {
    this.viewport = this.querySelector('[data-cross-sell="viewport"]');
    this.track = this.querySelector('[data-cross-sell="track"]');
    this.progress = this.querySelector('[data-cross-sell="progress"]');
    this.progressFill = this.querySelector('[data-cross-sell="progressFill"]');
    this.emptyText = this.querySelector('[data-cross-sell="emptyText"]');
  }

  /**
   * @param {AbortSignal} signal
   * @param {number} loadId
   */
  async #load(signal, loadId) {
    this.#cacheElements();
    if (!(this.viewport instanceof HTMLElement) || !(this.track instanceof HTMLElement)) return;

    if (!this.#isProductMode) {
      // Prefer fresh ids from the live cart after morphs.
      await this.#syncProductIdsFromCart(signal);
      if (signal.aborted || loadId !== this.#loadId || !this.isConnected) return;
    }

    this.#showLoading();

    const productIds = (this.dataset.productIds || '')
      .split(',')
      .map((id) => Number(id.trim()))
      .filter(Boolean);

    if (!productIds.length) {
      this.#products = [];
      this.#showEmpty();
      return;
    }

    const maxProducts = Number(this.dataset.limit || 8);
    const fetchLimit = maxProducts + productIds.length;
    const complementaryIntent = this.dataset.complementaryIntent || 'complementary';
    const fallbackIntent = this.dataset.fallbackIntent || 'related';
    const cartIdSet = new Set(productIds);

    /** @type {Array<Record<string, any>>} */
    let products = [];

    for (const intent of [complementaryIntent, fallbackIntent]) {
      for (const productId of productIds) {
        if (signal.aborted || loadId !== this.#loadId || !this.isConnected) return;

        const batch = await this.#fetchRecommendations(productId, fetchLimit, intent, signal);
        products = this.#mergeProducts(products, batch, cartIdSet, maxProducts);
        if (products.length >= maxProducts) break;
      }
      if (products.length >= maxProducts) break;
    }

    if (!products.length && !signal.aborted && loadId === this.#loadId && this.isConnected) {
      const collectionHandle = this.dataset.fallbackCollection || 'all';
      const batch = await this.#fetchCollectionProducts(collectionHandle, fetchLimit, signal);
      products = this.#mergeProducts(products, batch, cartIdSet, maxProducts);
    }

    if (signal.aborted || loadId !== this.#loadId || !this.isConnected) return;

    this.#products = products;

    if (!products.length) {
      this.#showEmpty();
      return;
    }

    this.#renderItems(products);
    this.#setupProgress();
  }

  /**
   * Keep data-product-ids in sync after cart morph/add.
   * @param {AbortSignal} signal
   */
  async #syncProductIdsFromCart(signal) {
    try {
      const response = await fetch('/cart.js', { credentials: 'same-origin', signal });
      if (!response.ok) return;
      const cart = await response.json();
      const ids = Array.isArray(cart.items)
        ? cart.items.map((item) => Number(item.product_id)).filter(Boolean)
        : [];
      if (ids.length) {
        this.dataset.productIds = [...new Set(ids)].join(',');
      }
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        // Keep existing dataset ids on failure.
      }
    }
  }

  /**
   * @param {Array<Record<string, any>>} existing
   * @param {Array<Record<string, any>>} batch
   * @param {Set<number>} cartIdSet
   * @param {number} maxProducts
   */
  #mergeProducts(existing, batch, cartIdSet, maxProducts) {
    const seen = new Set(existing.map((product) => product.id));
    const merged = [...existing];

    for (const product of batch) {
      const id = Number(product?.id);
      if (!id || cartIdSet.has(id) || seen.has(id)) continue;
      if (product.available === false) continue;
      seen.add(id);
      merged.push(product);
      if (merged.length >= maxProducts) break;
    }

    return merged;
  }

  /**
   * @param {number} productId
   * @param {number} limit
   * @param {string} intent
   * @param {AbortSignal} signal
   */
  async #fetchRecommendations(productId, limit, intent, signal) {
    const url = `/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${intent}`;
    try {
      const response = await fetch(url, { credentials: 'same-origin', signal });
      if (!response.ok) return [];
      const data = await response.json();
      return Array.isArray(data.products) ? data.products : [];
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return [];
      return [];
    }
  }

  /**
   * @param {string} handle
   * @param {number} limit
   * @param {AbortSignal} signal
   */
  async #fetchCollectionProducts(handle, limit, signal) {
    const url = `/collections/${encodeURIComponent(handle)}/products.json?limit=${limit}`;
    try {
      const response = await fetch(url, { credentials: 'same-origin', signal });
      if (!response.ok) return [];
      const data = await response.json();
      return Array.isArray(data.products) ? data.products : [];
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return [];
      return [];
    }
  }

  /**
   * @param {unknown} price
   */
  #formatMoney(price) {
    let cents = 0;
    if (typeof price === 'string') {
      cents = Math.round(parseFloat(price) * 100);
    } else {
      cents = Number(price) || 0;
    }

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, this.dataset.moneyFormat || undefined);
    }

    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(cents / 100);
  }

  /**
   * @param {Record<string, any>} product
   */
  #productImage(product) {
    const featured = product.featured_image;
    if (typeof featured === 'string') return featured;
    if (featured && typeof featured.src === 'string') return featured.src;
    if (Array.isArray(product.images) && typeof product.images[0] === 'string') return product.images[0];
    if (Array.isArray(product.images) && product.images[0]?.src) return product.images[0].src;
    return '';
  }

  /**
   * @param {Record<string, any>} product
   */
  #variantForProduct(product) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    return variants.find((variant) => variant?.available) || variants[0] || null;
  }

  /**
   * @param {Array<Record<string, any>>} products
   */
  #renderItems(products) {
    this.#cacheElements();
    if (!(this.viewport instanceof HTMLElement) || !(this.track instanceof HTMLElement)) return;

    const addLabel = this.dataset.addToCartLabel || 'Add';
    const soldOutLabel = this.dataset.soldOutLabel || 'Sold out';

    const html = products
      .map((product) => {
        const title = this.#escape(product.title || '');
        const image = this.#productImage(product);
        const url = this.#escape(product.url || `/products/${product.handle || ''}`);
        const variant = this.#variantForProduct(product);
        // The product resource JSON only carries price per-variant, not on the product itself.
        const price = this.#formatMoney(variant?.price ?? product.price ?? 0);
        const variantId = variant?.id ? String(variant.id) : '';
        const canAdd = Boolean(variantId && variant?.available !== false);
        const buttonLabel = canAdd ? addLabel : soldOutLabel;

        return `
          <article class="cart-cross-sell__item">
            <a class="cart-cross-sell__image-wrap" href="${url}">
              ${image ? `<img class="cart-cross-sell__image" src="${image}" alt="${title}" loading="lazy">` : ''}
            </a>
            <div class="cart-cross-sell__meta">
              <a class="cart-cross-sell__name" href="${url}">${title}</a>
              <span class="cart-cross-sell__price">${price}</span>
              <button
                type="button"
                class="button cart-cross-sell__add"
                data-variant-id="${variantId}"
                data-product-id="${product.id || ''}"
                ${canAdd ? '' : 'disabled'}
              >
                ${this.#escape(buttonLabel)}
              </button>
            </div>
          </article>
        `;
      })
      .join('');

    // Ignore MutationObserver for our own writes (observer microtasks run before the clear below).
    this.#updating = true;
    this.track.innerHTML = html;
    this.viewport.hidden = false;
    this.viewport.removeAttribute('aria-busy');
    if (this.progress) this.progress.hidden = false;
    if (this.emptyText) this.emptyText.classList.add('hidden');
    queueMicrotask(() => {
      this.#updating = false;
    });
  }

  #setupProgress() {
    this.#cacheElements();
    if (!(this.viewport instanceof HTMLElement) || !(this.progressFill instanceof HTMLElement)) return;

    const update = () => {
      if (!(this.viewport instanceof HTMLElement) || !(this.progressFill instanceof HTMLElement)) return;
      const maxScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
      if (maxScroll <= 0) {
        this.progressFill.style.width = '38%';
        return;
      }

      const ratio = this.viewport.scrollLeft / maxScroll;
      const min = 24;
      const max = 100;
      const width = min + ratio * (max - min);
      this.progressFill.style.width = `${Math.max(min, Math.min(max, width))}%`;
    };

    update();
    this.viewport.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
  }

  #showLoading() {
    this.#cacheElements();
    if (this.emptyText) this.emptyText.classList.add('hidden');
    if (this.progress) this.progress.hidden = true;
    if (this.viewport instanceof HTMLElement) {
      this.viewport.hidden = false;
      this.viewport.setAttribute('aria-busy', 'true');
    }
    if (this.track instanceof HTMLElement) {
      this.#updating = true;
      this.track.innerHTML = `
        <div class="cart-cross-sell__item cart-cross-sell__item--skeleton" aria-hidden="true"></div>
        <div class="cart-cross-sell__item cart-cross-sell__item--skeleton" aria-hidden="true"></div>
      `;
      queueMicrotask(() => {
        this.#updating = false;
      });
    }
  }

  #showEmpty() {
    this.#cacheElements();
    if (this.emptyText instanceof HTMLElement) {
      this.emptyText.classList.remove('hidden');
      this.emptyText.textContent = this.dataset.emptyText || '';
    }
    if (this.viewport instanceof HTMLElement) {
      this.viewport.hidden = true;
      this.viewport.removeAttribute('aria-busy');
    }
    if (this.track instanceof HTMLElement) {
      this.#updating = true;
      this.track.innerHTML = '';
      queueMicrotask(() => {
        this.#updating = false;
      });
    }
    if (this.progress instanceof HTMLElement) this.progress.hidden = true;
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('.cart-cross-sell__add');
    if (!(button instanceof HTMLButtonElement)) return;
    event.preventDefault();
    event.stopPropagation();
    if (button.disabled) return;
    this.#addToCart(button);
  };

  /**
   * @param {HTMLButtonElement} button
   */
  async #addToCart(button) {
    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    const originalLabel = button.textContent;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = this.dataset.addingLabel || 'Adding…';

    const sectionIds = [
      ...new Set(
        Array.from(document.querySelectorAll('cart-items-component'))
          .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
          .filter(Boolean)
      ),
    ];

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }),
        })
      );
      const data = await response.json();

      if (data.status || data.message || data.errors) {
        button.textContent = this.dataset.errorLabel || 'Unavailable';
        window.setTimeout(() => {
          button.textContent = originalLabel;
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }, 1500);
        return;
      }

      button.textContent = this.dataset.addedLabel || 'Added';

      this.dispatchEvent(
        new CartAddEvent(data, this.id || 'cart-cross-sell', {
          source: 'cart-cross-sell',
          productId: button.dataset.productId,
          variantId: String(variantId),
          itemCount: Array.isArray(data.items) ? data.items.length : 1,
          sections: data.sections,
        })
      );

      window.setTimeout(() => {
        if (!button.isConnected) return;
        button.textContent = originalLabel;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }, 1200);
    } catch (_error) {
      button.textContent = this.dataset.errorLabel || 'Unavailable';
      window.setTimeout(() => {
        button.textContent = originalLabel;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }, 1500);
    }
  }

  /**
   * @param {string} value
   */
  #escape(value) {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }
}

if (!customElements.get('cart-cross-sell')) {
  customElements.define('cart-cross-sell', CartCrossSell);
}
